import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import {
  type ConsentCategories,
  DENIED_CONSENT,
  FULL_CONSENT,
  readConsent,
  saveConsent,
} from '@/lib/consent'

type ConsentContextValue = {
  /** Current consent choice. Defaults to all-denied until the visitor responds. */
  categories: ConsentCategories
  /** False until the visitor makes a choice (drives the banner's visibility). */
  hasResponded: boolean
  /** True once the provider has hydrated on the client (avoids SSR/prerender flash). */
  ready: boolean
  /** Whether the preferences modal is open. */
  preferencesOpen: boolean
  acceptAll: () => void
  rejectAll: () => void
  savePreferences: (categories: ConsentCategories) => void
  openPreferences: () => void
  closePreferences: () => void
}

const ConsentContext = createContext<ConsentContextValue | null>(null)

export function ConsentProvider({ children }: { children: React.ReactNode }) {
  const [categories, setCategories] = useState<ConsentCategories>(DENIED_CONSENT)
  const [hasResponded, setHasResponded] = useState(false)
  const [ready, setReady] = useState(false)
  const [preferencesOpen, setPreferencesOpen] = useState(false)

  // Hydrate from storage on mount (client only).
  useEffect(() => {
    const stored = readConsent()
    if (stored) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- hydrating from localStorage on mount. Reading storage during render would desync server and client markup, so the mount effect is the correct place for this.
      setCategories(stored)
      setHasResponded(true)
    }
    setReady(true)
  }, [])

  const commit = useCallback((next: ConsentCategories) => {
    saveConsent(next)
    setCategories(next)
    setHasResponded(true)
    setPreferencesOpen(false)
  }, [])

  const acceptAll = useCallback(() => commit(FULL_CONSENT), [commit])
  const rejectAll = useCallback(() => commit(DENIED_CONSENT), [commit])
  const savePreferences = useCallback(
    (next: ConsentCategories) => commit({ ...next, necessary: true }),
    [commit],
  )
  const openPreferences = useCallback(() => setPreferencesOpen(true), [])
  const closePreferences = useCallback(() => setPreferencesOpen(false), [])

  const value = useMemo<ConsentContextValue>(
    () => ({
      categories,
      hasResponded,
      ready,
      preferencesOpen,
      acceptAll,
      rejectAll,
      savePreferences,
      openPreferences,
      closePreferences,
    }),
    [
      categories,
      hasResponded,
      ready,
      preferencesOpen,
      acceptAll,
      rejectAll,
      savePreferences,
      openPreferences,
      closePreferences,
    ],
  )

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>
}

export function useConsent(): ConsentContextValue {
  const ctx = useContext(ConsentContext)
  if (!ctx) {
    throw new Error('useConsent must be used within a ConsentProvider')
  }
  return ctx
}
